import {cloneDeep} from "lodash-es";
import {logError} from "../error/index.js";

// Type definitions for Segment configuration
interface SegmentConfig {
    writeKey: string;
    jsSrc: string;
    cdn: string;
}

// Type declarations for window extensions
declare global {
    interface Window {
        analytics?: AnalyticsInstance;
        appSettings?: {
            segment?: SegmentConfig;
        };
        [key: string]: unknown;
    }
}

interface AnalyticsInstance {
    [key: string]: unknown;
    initialize?: boolean;
    invoked?: boolean;
    methods?: string[];
    factory?: (method: string) => (...args: unknown[]) => AnalyticsInstance;
    load?: (key: string, options?: unknown) => void;
    page?: () => void;
    push?: (args: unknown[]) => void;
    addSourceMiddleware?: (middleware: MiddlewareFunction) => void;
    _loadOptions?: unknown;
    _writeKey?: string;
    _cdn?: string;
    SNIPPET_VERSION?: string;
    initialized?: boolean;
}

interface MiddlewareFunction {
    (context: { payload: PayloadObject; next: (payload: PayloadObject) => void }): void;
}

interface PayloadObject {
    obj: {
        event?: string;
        context?: {
            event_id?: string;
        };
        messageId?: string;
    };
}

function initSegment(config: SegmentConfig) {
    const i = "analytics", analytics = window[i] = window[i] || [];
    if (!analytics.initialize) if (analytics.invoked) window.console && console.error && console.error("Segment snippet included twice."); else {
        analytics.invoked = !0;
        analytics.methods = ["trackSubmit", "trackClick", "trackLink", "trackForm", "pageview", "identify", "reset", "group", "track", "ready", "alias", "debug", "page", "screen", "once", "off", "on", "addSourceMiddleware", "addIntegrationMiddleware", "setAnonymousId", "addDestinationMiddleware", "register"];
        analytics.factory = function (e) {
            return function () {
                // eslint-disable-next-line prefer-spread,prefer-rest-params
                if (window[i].initialized) return window[i][e].apply(window[i], arguments);
                // eslint-disable-next-line prefer-rest-params
                const n = Array.prototype.slice.call(arguments);
                if (["track", "screen", "alias", "group", "page", "identify"].indexOf(e) > -1) {
                    const c = document.querySelector("link[rel='canonical']");
                    n.push({
                        __t: "bpc",
                        c: c && c.getAttribute("href") || void 0,
                        p: location.pathname,
                        u: location.href,
                        s: location.search,
                        t: document.title,
                        r: document.referrer
                    });
                }
                n.unshift(e);
                analytics.push(n);
                return analytics;
            };
        };
        for (let n = 0; n < analytics.methods.length; n++) {
            const key = analytics.methods[n];
            analytics[key] = analytics.factory(key);
        }
        analytics.load = function (key, n) {
            const t = document.createElement("script");
            t.type = "text/javascript";
            t.async = !0;
            t.setAttribute("data-global-segment-analytics-key", i);
            t.src = config.jsSrc;
            const r = document.getElementsByTagName("script")[0];
            r.parentNode.insertBefore(t, r);
            analytics._loadOptions = n;
        };
        analytics._writeKey = config.writeKey;
        analytics._cdn = config.cdn;
        analytics.SNIPPET_VERSION = "5.2.0";
        analytics.load(config.writeKey);
        analytics.page();

        setMessageIdOverwrite(analytics);
    }
}

function setMessageIdOverwrite(analytics: AnalyticsInstance) {
    const eventsToOverwrite = ["Song Purchased", "Order Completed", "Item Purchased"];

    analytics.addSourceMiddleware?.(({payload, next}) => {
        const event = payload.obj;
        if (event.event && eventsToOverwrite.includes(event.event) && event.context?.event_id) {
            event.messageId = event.context.event_id;
        }
        next(payload);
    });
}

export default function analyticsEvent(event: string, details?: unknown): void {
    try {
        const segmentConf = window.appSettings?.segment;
        if (!window.analytics && segmentConf?.writeKey && segmentConf?.jsSrc && segmentConf?.cdn) {
            initSegment(segmentConf);
        }
    
        document.dispatchEvent(new CustomEvent(event, {
            detail: cloneDeep(details)
        }));
    } catch (error) {
        // console.error("Failed to send", error);
        logError(error);
    }
}
